// /opt/isp_erp/frontend/src/components/tickets/EquiposIncidente.jsx
import React, { useEffect, useRef, useState } from 'react';
import {
  Alert, Autocomplete, Box, Button, Chip, CircularProgress, Stack, TextField, ToggleButton, ToggleButtonGroup, Typography
} from '@mui/material';
import axios from '../../api/axios';

const etiqueta = (e) => (e ? `${e.sn}${e.modelo ? ` — ${e.modelo}` : ''}` : '');

/**
 * Equipos de un incidente: retirar uno del cliente, cambiarlo por uno del técnico o instalar uno más.
 * value = { retirar: [{sn, modelo, tipo, motivo}], instalar: [{sn, modelo, tipo}], comentario: '' }
 */
const EquiposIncidente = ({ ticketId, value, onChange, disabled = false }) => {
  const retirar = (value && value.retirar) || [];
  const instalar = (value && value.instalar) || [];
  const comentario = (value && value.comentario) || '';
  const [accion, setAccion] = useState('retirar');
  const [delCliente, setDelCliente] = useState([]);
  const [viejo, setViejo] = useState(null);
  const [nuevo, setNuevo] = useState(null);
  const [motivo, setMotivo] = useState('');
  const [texto, setTexto] = useState('');
  const [opciones, setOpciones] = useState([]);
  const [abierto, setAbierto] = useState(false);
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState('');
  const contador = useRef(0);

  const necesitaViejo = accion === 'retirar' || accion === 'cambiar';
  const necesitaNuevo = accion === 'instalar' || accion === 'cambiar';

  useEffect(() => {
    if (!ticketId) return undefined;
    let vigente = true;
    axios.get(`/tickets/${ticketId}/equipos-cliente`)
      .then((r) => { if (vigente) setDelCliente(r.data.items || []); })
      .catch(() => { if (vigente) setDelCliente([]); });
    return () => { vigente = false; };
  }, [ticketId]);

  useEffect(() => {
    if (!abierto || !necesitaNuevo || !ticketId) return undefined;
    const id = ++contador.current;
    const espera = setTimeout(async () => {
      setCargando(true);
      try {
        const r = await axios.get(`/tickets/${ticketId}/equipos-tecnico`, { params: texto.trim() ? { q: texto.trim() } : {} });
        if (id === contador.current) setOpciones(r.data.items || []);
      } catch (e) {
        if (id === contador.current) setOpciones([]);
      } finally {
        if (id === contador.current) setCargando(false);
      }
    }, 300);
    return () => clearTimeout(espera);
  }, [abierto, texto, necesitaNuevo, ticketId]);

  const agregar = () => {
    setError('');
    if (necesitaViejo && !viejo) { setError('Elige el equipo del cliente que se retira'); return; }
    if (necesitaNuevo && !nuevo) { setError('Elige el equipo del técnico que se instala'); return; }
    const nuevosRetirar = [...retirar];
    const nuevosInstalar = [...instalar];
    if (necesitaViejo) {
      if (nuevosRetirar.some((e) => e.sn === viejo.sn)) { setError('Ese equipo ya está en la lista de retiro'); return; }
      nuevosRetirar.push({ sn: viejo.sn, modelo: viejo.modelo, tipo: viejo.tipo, motivo: motivo.trim() });
    }
    if (necesitaNuevo) {
      if (nuevosInstalar.some((e) => e.sn === nuevo.sn)) { setError('Ese equipo ya está en la lista de instalación'); return; }
      nuevosInstalar.push({ sn: nuevo.sn, modelo: nuevo.modelo, tipo: nuevo.tipo });
    }
    onChange({ retirar: nuevosRetirar, instalar: nuevosInstalar, comentario });
    setViejo(null);
    setNuevo(null);
    setMotivo('');
    setTexto('');
  };

  const disponiblesDelCliente = delCliente.filter((e) => !retirar.some((r) => r.sn === e.sn));
  const disponiblesDelTecnico = opciones.filter((e) => !instalar.some((r) => r.sn === e.sn));

  return (
    <Box sx={{ p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1, mb: 2 }}>
      <ToggleButtonGroup
        exclusive
        size="small"
        color="primary"
        value={accion}
        onChange={(_, v) => { if (v) { setAccion(v); setError(''); } }}
        disabled={disabled}
        sx={{ flexWrap: 'wrap', mb: 1.5 }}
      >
        <ToggleButton value="retirar">Retirar equipo</ToggleButton>
        <ToggleButton value="cambiar">Cambiar equipo</ToggleButton>
        <ToggleButton value="instalar">Instalar un equipo más</ToggleButton>
      </ToggleButtonGroup>

      <Stack spacing={1.5}>
        {necesitaViejo && (
          <Autocomplete
            size="small"
            options={disponiblesDelCliente}
            value={viejo}
            onChange={(_, v) => setViejo(v)}
            getOptionLabel={etiqueta}
            isOptionEqualToValue={(o, v) => o.sn === v.sn}
            noOptionsText="El cliente no tiene equipos instalados registrados"
            renderInput={(params) => <TextField {...params} label="Equipo instalado al cliente (el que se retira)" />}
          />
        )}
        {necesitaViejo && (
          <TextField size="small" label="Motivo del retiro (opcional)" value={motivo} onChange={(e) => setMotivo(e.target.value)} />
        )}
        {necesitaNuevo && (
          <Autocomplete
            size="small"
            open={abierto}
            onOpen={() => setAbierto(true)}
            onClose={() => setAbierto(false)}
            options={disponiblesDelTecnico}
            value={nuevo}
            loading={cargando}
            filterOptions={(o) => o}
            onChange={(_, v) => setNuevo(v)}
            onInputChange={(_, v, motivoCambio) => {
              if (motivoCambio === 'input') setTexto(v);
              if (motivoCambio === 'clear') setTexto('');
            }}
            getOptionLabel={etiqueta}
            isOptionEqualToValue={(o, v) => o.sn === v.sn}
            noOptionsText={cargando ? 'Buscando...' : 'El técnico no tiene equipos asignados que coincidan'}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Equipo del técnico que se instala (escribe para buscar)"
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (<>{cargando ? <CircularProgress size={16} /> : null}{params.InputProps.endAdornment}</>),
                }}
              />
            )}
          />
        )}
        {error && <Alert severity="warning" sx={{ py: 0 }}>{error}</Alert>}
        <Box>
          <Button variant="contained" size="small" onClick={agregar} disabled={disabled}>Agregar a la lista</Button>
        </Box>
      </Stack>

      {(retirar.length > 0 || instalar.length > 0) && (
        <Box sx={{ mt: 2 }}>
          {retirar.length > 0 && (
            <Box sx={{ mb: 1 }}>
              <Typography variant="caption" color="text.secondary">Se retirarán del cliente</Typography>
              <Stack direction="row" flexWrap="wrap" gap={0.75} sx={{ mt: 0.5 }}>
                {retirar.map((e) => (
                  <Chip key={e.sn} color="warning" variant="outlined" label={etiqueta(e)}
                        onDelete={disabled ? undefined : () => onChange({ retirar: retirar.filter((x) => x.sn !== e.sn), instalar, comentario })} />
                ))}
              </Stack>
            </Box>
          )}
          {instalar.length > 0 && (
            <Box>
              <Typography variant="caption" color="text.secondary">Se instalarán al cliente</Typography>
              <Stack direction="row" flexWrap="wrap" gap={0.75} sx={{ mt: 0.5 }}>
                {instalar.map((e) => (
                  <Chip key={e.sn} color="success" variant="outlined" label={etiqueta(e)}
                        onDelete={disabled ? undefined : () => onChange({ retirar, instalar: instalar.filter((x) => x.sn !== e.sn), comentario })} />
                ))}
              </Stack>
            </Box>
          )}
        </Box>
      )}

      <TextField
        fullWidth
        multiline
        minRows={2}
        size="small"
        label="Comentario sobre los equipos (opcional)"
        placeholder="Ej: el cliente cambió de router por falla de puertos"
        value={comentario}
        onChange={(e) => onChange({ retirar, instalar, comentario: e.target.value })}
        inputProps={{ maxLength: 500 }}
        disabled={disabled}
        sx={{ mt: 2 }}
      />
    </Box>
  );
};

export default EquiposIncidente;
